import React, { useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';
import { useCartData } from '../../context/CartDataContext';
import { useRegistration } from '../../context/RegistrationContext';
import { useUserState } from '@/components/context/UserContext';
import {
  IsIncorrectChild,
  priceForService,
  priceForServiceBeforeDiscount,
  sumAdditionalServices,
} from '@/v2/utils/priceUtils';
import { InfoTooltipCard } from '@/v2/components/SpecialistInfoCard/InfoTooltipCard';
import { CircleAlert, X } from 'lucide-react';
import { isSGBySpecialty } from '@/v2/utils/commonServiceClassificationRulesUtils';
import { useSettingsState } from '@/components/context/SettingsContext';


const formatPrice = (eur) =>
  new Intl.NumberFormat('lt-LT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(eur) || 0);

export default function PaymentSummaryCard() {
  const { consultationInfo, userInfo } = useRegistration();

  const userState = useUserState();
  const [showAdditionalLabServices, setShowAdditionalLabServices] =
    React.useState(false);
  const {
    service,
    servicesData,
    services,
    selectedTimeSlot,
    additionalServices = [],
    setServices,
  } = useCartData?.() || {};

  const { agreements } = useSettingsState();

  const svc = React.useMemo(() => {
    // if (service && typeof service === 'object') return service;
    if (!services) return undefined;
    return Array.isArray(services) ? services[0] : service;
  }, [service, services]);

  const additionalSvc = React.useMemo(() => {
    if (!services) return undefined;
    if (Array.isArray(services) && services.length > 1) {
      setShowAdditionalLabServices(true);
    }
    return Array.isArray(services) ? services.slice(1) : service;
  }, [service, services]);

  const isPaid = !!selectedTimeSlot?.isPaid;
  const isExtra = !!selectedTimeSlot?.isExtra;
 const GSKServiceTypeExternalId = process.env.REACT_APP_GSK_SERVICE_TYPE_EXT_ID;
  const VGSKServiceTypeExternalId = process.env.REACT_APP_VGSK_SERVICE_TYPE_EXT_ID;
  const hasSecondaryOption = !isExtra && svc?.secondaryPrice != null;
  const visitType = isExtra
    ? 'extra'
    : hasSecondaryOption
    ? consultationInfo?.visitType || 'primary'
    : 'primary';

  const title = !isPaid
    ? 'Paslauga PSDF lėšomis'
    : visitType === 'extra'
    ? 'Papildoma paslauga'
    : visitType === 'secondary'
    ? 'Pakartotinė konsultacija'
    : 'Pirminė konsultacija';

  const isIncorrectChild = IsIncorrectChild(userState, userInfo);

  const mainPrice = priceForService({
    svc,
    visitType,
    isExtraSlot: isExtra,
    psdf: !isPaid,
    isIncorrectChild,
  });

  const mainPriceBeforeDiscount = priceForServiceBeforeDiscount({
    svc,
    visitType,
    isExtraSlot: isExtra,
    psdf: !isPaid,
    isIncorrectChild,
  });

  const additionalsTotal = sumAdditionalServices(additionalServices);
  const additionalLabServiceTotal = isPaid
    ? sumAdditionalServices(additionalSvc)
    : 0;
  const showAdditionals = additionalsTotal > 0;
  const total = mainPrice + additionalsTotal + additionalLabServiceTotal;
  const hasIncreasedPrice=  svc?.serviceTypeExternalId == GSKServiceTypeExternalId ||svc?.serviceTypeExternalId == VGSKServiceTypeExternalId; 
  return (
    <Card>
      <CardHeader>
        <Heading variant="3">Apmokėjimo informacija</Heading>
      </CardHeader>

      <CardContent className="space-y-2">
        <div className="flex items-start gap-2 text-sm">
         
          <div>
 <CircleAlert className="w-5 h-5 text-primary" />

          </div>
           {hasIncreasedPrice ?  <span className="leading-5 text-primary font-primary">
         Konsultacijos metu gali būti reikalingos papildomos paslaugos, kurios neįskaičiuotos į pasirinktos paslaugos kainą: {' '}
          <a
            href="https://affidea.lt/lt/kainos/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Rodyti
          </a>
          .
        </span> :  <span className="leading-5 text-primary font-primary">
         Paslaugai suteikti gali būti reikalingos papildomos paslaugos. Visos
          kainos{' '}

          <a
            href="https://affidea.lt/lt/kainos/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            čia
          </a>
          .
        </span>}
      </div>
        <div className="flex items-center justify-between">
          <div className="text-black">{service?.serviceTitle}</div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-primary font-bold">
            <span className="whitespace-nowrap">{hasIncreasedPrice ? 'nuo ' : ''}{formatPrice(mainPrice)}</span>

            {mainPriceBeforeDiscount && mainPriceBeforeDiscount > mainPrice && (
              <span className="text-gray-400 line-through font-normal whitespace-nowrap">
                {formatPrice(mainPriceBeforeDiscount)}
              </span>
            )}

            {!selectedTimeSlot?.isPaid &&
              (isSGBySpecialty(service?.specialtyExternalId) ? (
                <InfoTooltipCard>
                  Paslauga kompensuojama PSDF lėšomis tik prisirašiusiems
                  pacientams ir tik pasirinkus "PSDF kompensuojamas laikas"
                </InfoTooltipCard>
              ) : (
                <InfoTooltipCard>
                  Iš PSDF biudžeto kompensuojamos paslaugos teikiamos pacientams
                  nemokamai tik tuo atveju, jeigu pacientas pateikia gydytojo
                  išduotą siuntimą ir yra visos teisės aktuose nurodytos
                  prielaidos (sąlygos), kurioms esant atitinkamos paslaugos gali
                  būti teikiamos pacientams nemokamai. Daugiau informacijos:{' '}
                  <a
                    href={agreements.clinicRules.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    čia
                  </a>
                </InfoTooltipCard>
              ))}
          </div>
        </div>

        {showAdditionalLabServices && additionalSvc?.length > 0 && (
          <div className="mt-2">
            {/* Header row with title and total */}
            <div className="flex items-center justify-between mb-2">
              <div className="text-black font-semibold">
                Papildomi laboratoriniai tyrimai
              </div>
              <div className="text-primary font-bold">
                {formatPrice(additionalLabServiceTotal)}
              </div>
            </div>

            {/* List of additional services */}
            <div className="space-y-1">
              {additionalSvc.map((s, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="text-black flex ">
                    <X
                      className="text-red-500 cursor-pointer"
                      onClick={() => {
                        // Handle remove service
                        setServices((prev) =>
                          prev.filter((service) => service !== s),
                        );
                      }}
                    />
                    {s.serviceTitle}
                  </div>
                  <div className="text-primary font-bold">
                    {formatPrice(isPaid ? s?.priceWithDiscount || s?.price : 0)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showAdditionals && (
          <div className="flex ">
            <div className="text-black font-bold">
              Papildomos privalomos paslaugos:
            </div>
            {/* <div className="text-primary font-bold">
              {formatPrice(additionalsTotal)}
            </div> */}
          </div>
        )}

        {showAdditionals &&
          additionalServices &&
          additionalServices.length > 0 && (
            <div className="flex  justify-between flex-col">
              {additionalServices.map((service, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="text-black"> {service.name}</div>
                  <div className="text-primary font-bold">
                    {formatPrice(service.price)}
                  </div>
                </div>
              ))}
            </div>
          )}
      </CardContent>

      <CardFooter
        className={showAdditionals ? 'p-0 px-2 sm:px-4 text-xl py-4' : ''}
      >
        {showAdditionals && (
          <div className="flex w-full items-center justify-between">
            <div className="text-black">Iš viso su PVM</div>
            <div className="text-primary font-bold">{formatPrice(total)}</div>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
